import React, { forwardRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { IP_CHART_PALETTE } from './IpLineChart';

// Growth Barometer as a stacked monthly bar chart, matching the Word report
// (the four CAI components stack per month; navy/gold/grey/light blue).
// data = { dates: ['YYYY-MM-DD', ...], series: [{ label, values: [...] }] }
const IpBarometerChart = forwardRef(({ data, title = null }, ref) => {
  if (!data || !Array.isArray(data.series) || data.series.length === 0 || !Array.isArray(data.dates)) {
    return <div className="chart-placeholder">No data available</div>;
  }

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const labels = data.dates.map((d) => {
    const s = String(d);
    return `${MONTHS[Number(s.slice(5, 7)) - 1]}-${s.slice(2, 4)}`;
  });

  const datasets = data.series.map((s, i) => ({
    label: s.label,
    data: s.values.map((v) => (v == null ? null : v)),
    backgroundColor: s.colour || IP_CHART_PALETTE[i % IP_CHART_PALETTE.length],
    borderWidth: 0,
    // A little air between the monthly stacks, like the Word bars
    categoryPercentage: 0.8,
    barPercentage: 0.9,
  }));

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: 0 },
    interaction: { intersect: false, mode: 'index' },
    plugins: {
      legend: {
        position: 'bottom',
        align: 'center',
        labels: { font: { size: 10 }, boxWidth: 9, boxHeight: 9, padding: 8, color: '#212529' },
      },
      tooltip: {
        mode: 'index', intersect: false,
        callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y == null ? '—' : ctx.parsed.y.toFixed(2)}` },
      },
    },
    scales: {
      x: {
        stacked: true,
        border: { display: false },
        grid: { display: false },
        ticks: { font: { size: 10 }, color: '#212529', maxTicksLimit: 11, maxRotation: 0, minRotation: 0 },
      },
      y: {
        stacked: true,
        border: { display: false },
        grid: { color: 'rgba(0,0,0,0.10)' },
        // precision: 0 keeps tick steps on whole numbers (0.5 steps rounded to
        // 0dp were producing duplicated axis labels)
        ticks: { font: { size: 10 }, color: '#212529', precision: 0, callback: (v) => Number(v).toFixed(0) },
      },
    },
  };

  return (
    <div className="ip-chart-panel">
      {title && <div className="ip-chart-title">{title}</div>}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <Bar ref={ref} data={{ labels, datasets }} options={options} />
      </div>
    </div>
  );
});

IpBarometerChart.displayName = 'IpBarometerChart';
export default IpBarometerChart;
